import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { useConfigCadastro } from './hooks/useConfigCadastro';
import { ProtectedRoute } from './routes/ProtectedRoute';

// Venda de pacotes desligada (config/cadastro.pacotesAtivo): o cadastro avulso volta para o login.
function RotaVendaPacotes({ children }: { children: React.ReactElement }) {
  const { pacotesAtivo, carregando } = useConfigCadastro();
  if (carregando) return null;
  return pacotesAtivo ? children : <Navigate to="/login" replace />;
}

// --- Public Routes ---
const LoginV2 = React.lazy(() => import('./pages/Auth/LoginV2'));
const SignupV2 = React.lazy(() => import('./pages/Auth/SignupV2'));
const SignupGoogle = React.lazy(() => import('./pages/Auth/SignupGoogle'));
const SignupAvulso = React.lazy(() => import('./pages/Auth/SignupAvulso'));
const PoliticaPrivacidade = React.lazy(() => import('./pages/PoliticaPrivacidade'));
const EventoInscricao = React.lazy(() => import('./pages/Eventos/Inscricao'));
const EventosLista = React.lazy(() => import('./pages/Eventos/Lista'));
const PacotesVendas = React.lazy(() => import('./pages/Pacotes/Vendas'));

// --- Layouts ---
const AdmLayout = React.lazy(() => import('./layouts/AdmLayout/AdmLayout'));
const StudentLayout = React.lazy(() => import('./layouts/StudentLayout/StudentLayout'));


// --- Admin Section ---
const AdminDashboard = React.lazy(() => import('./pages/AdminV2/Dashboard'));
const AdminStudentsReports = React.lazy(() => import('./pages/AdminV2/Students/Reports'));
const AdminStudentsPayments = React.lazy(() => import('./pages/AdminV2/Students/Payments'));
const AdminContratos = React.lazy(() => import('./pages/AdminV2/Contratos/Index'));
const AdminContratoModelo = React.lazy(() => import('./pages/AdminV2/Contratos/Modelo'));
const AdminStudentsEdit = React.lazy(() => import('./pages/AdminV2/Students/Edit'));
const AdminStudentsNew = React.lazy(() => import('./pages/AdminV2/Students/New'));

const AdminCoursesReports = React.lazy(() => import('./pages/AdminV2/Courses/Reports'));
const AdminCoursesNew = React.lazy(() => import('./pages/AdminV2/Courses/New'));
const AdminUsersReports = React.lazy(() => import('./pages/AdminV2/Users/Reports'));

const AdminSchedulesNew = React.lazy(() => import('./pages/AdminV2/Schedules/New'));
const AdminSchedulesView = React.lazy(() => import('./pages/AdminV2/Schedules/View'));

const AdminPackagesIndex = React.lazy(() => import('./pages/AdminV2/Packages/Index'));
const AdminPackagesCompradores = React.lazy(() => import('./pages/AdminV2/Packages/Compradores'));
const AdminMaterialsIndex = React.lazy(() => import('./pages/AdminV2/Platform/Materials'));
const AdminSettingsIndex = React.lazy(() => import('./pages/AdminV2/Platform/Settings'));
const AdminLimpezaCadastro = React.lazy(() => import('./pages/AdminV2/Platform/LimpezaCadastro'));
const AdminAnoLetivoIndex = React.lazy(() => import('./pages/AdminV2/AnoLetivo/Index'));
const AdminConfiguracoesIndex = React.lazy(() => import('./pages/AdminV2/Configuracoes/Index'));

// --- Admin Section: Simula Edu (simulados e redação online) ---
const AdminSimuladosDashboard = React.lazy(() => import('./pages/AdminV2/Simulados/Dashboard'));
const AdminSimuladosConfig = React.lazy(() => import('./pages/AdminV2/Simulados/Config'));

// --- Admin Section: Eventos (aulões etc. com inscrição pública própria) ---
const AdminEventosReports = React.lazy(() => import('./pages/AdminV2/Eventos/Reports'));
const AdminEventosNew = React.lazy(() => import('./pages/AdminV2/Eventos/New'));
const AdminEventosInscritos = React.lazy(() => import('./pages/AdminV2/Eventos/Inscritos'));

// --- Student Section ---
const StudentDashboard = React.lazy(() => import('./pages/StudentV2/Dashboard'));
const StudentSchedules = React.lazy(() => import('./pages/StudentV2/Schedules'));
const StudentPayments = React.lazy(() => import('./pages/StudentV2/Payments'));
const StudentPackages = React.lazy(() => import('./pages/StudentV2/Packages'));
const StudentRedacaoPacote = React.lazy(() => import('./pages/StudentV2/RedacaoPacote'));
const StudentContrato = React.lazy(() => import('./pages/StudentV2/Contrato'));
const StudentMatricular = React.lazy(() => import('./pages/StudentV2/Matricular'));

// --- Student Section: Gamified Materials Hub ---
const MaterialsHubLayout = React.lazy(() => import('./pages/StudentV2/Materials/MaterialsHubLayout'));
const MaterialsOverview = React.lazy(() => import('./pages/StudentV2/Materials/MaterialsOverview'));
const StudentMaterials = React.lazy(() => import('./pages/StudentV2/Materials/StudentMaterials'));
const MyEssays = React.lazy(() => import('./pages/StudentV2/Materials/MyEssays'));
const Achievements = React.lazy(() => import('./pages/StudentV2/Materials/Achievements'));

// --- Student Section: Simula Edu (simulados e redação online) ---
const StudentSimuladosDashboard = React.lazy(() => import('./pages/StudentV2/Simulados/Dashboard'));

// --- Student Section: Correção de Redação por IA (autoatendimento, feature separada do Simula Edu) ---
const StudentRedacaoIA = React.lazy(() => import('./pages/StudentV2/RedacaoIA/Dashboard'));





const AppRouter: React.FC = () => {
  return (
    <Router>
      <React.Suspense fallback={
        <div className="flex h-screen w-full items-center justify-center bg-[#F8FAFC]">
          <div className="flex flex-col items-center gap-4">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#007b9c]/30 border-t-[#007b9c]"></div>
            <p className="text-gray-500 font-medium animate-pulse text-sm">Carregando painel...</p>
          </div>
        </div>
      }>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LoginV2 />} />
          <Route path="/login" element={<LoginV2 />} />
          <Route path="/signup" element={<SignupV2 />} />
          <Route path="/signup-google" element={<SignupGoogle />} />
          <Route path="/cadastro-avulso" element={<RotaVendaPacotes><SignupAvulso /></RotaVendaPacotes>} />
          <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
          <Route path="/pacotes" element={<RotaVendaPacotes><PacotesVendas /></RotaVendaPacotes>} />
          <Route path="/inscricao" element={<EventosLista />} />
          <Route path="/inscricao/:slug" element={<EventoInscricao />} />

          {/* Admin Protected Routes */}
          <Route element={<ProtectedRoute allowedRoles={['admin', 'adm', 'corretor', 'professor', 'secretaria']} />}>
            <Route path="/admin" element={<AdmLayout />}>
              <Route index element={<AdminDashboard />} />

              {/* Sub-rotas de Alunos */}
              <Route path="students/new" element={<AdminStudentsNew />} />
              <Route path="students/reports" element={<AdminStudentsReports />} />
              <Route path="students/payments" element={<AdminStudentsPayments />} />
              <Route element={<ProtectedRoute allowedRoles={['admin', 'adm', 'secretaria', 'professor']} />}>
                <Route path="contratos" element={<AdminContratos />} />
              </Route>
              <Route element={<ProtectedRoute allowedRoles={['admin', 'adm', 'secretaria', 'professor']} />}>
                <Route path="contratos/modelo" element={<AdminContratoModelo />} />
              </Route>
              <Route path="students/edit" element={<AdminStudentsEdit />} />

              {/* Sub-rotas de Cursos */}
              <Route path="courses/reports" element={<AdminCoursesReports />} />
              <Route path="courses/new" element={<AdminCoursesNew />} />
              <Route path="users/reports" element={<AdminUsersReports />} />

              {/* Sub-rotas de Atendimentos */}
              <Route path="schedules/new" element={<AdminSchedulesNew />} />
              <Route path="schedules/view" element={<AdminSchedulesView />} />

              {/* Sub-rotas de Plataforma */}
              <Route path="packages" element={<AdminPackagesIndex />} />
              <Route path="packages/:pacoteId/compradores" element={<AdminPackagesCompradores />} />
              <Route path="platform/materials" element={<AdminMaterialsIndex />} />
              <Route path="platform/settings" element={<AdminSettingsIndex />} />
              <Route element={<ProtectedRoute allowedRoles={['admin', 'adm', 'secretaria', 'professor']} />}>
                <Route path="platform/limpeza-cadastro" element={<AdminLimpezaCadastro />} />
              </Route>

              {/* Ano Letivo e Configurações (chave PIX, reCAPTCHA): só admin/adm (abrir temporada, apagar ano, expurgo LGPD). A guarda é mais
                  restrita que a do resto de /admin e o servidor (api/ano-letivo.js) valida de novo. */}
              <Route element={<ProtectedRoute allowedRoles={['admin', 'adm']} />}>
                <Route path="ano-letivo" element={<AdminAnoLetivoIndex />} />
                <Route path="configuracoes" element={<AdminConfiguracoesIndex />} />
              </Route>

              {/* Sub-rotas de Simula Edu (simulados e redação online) */}
              <Route path="simulados" element={<AdminSimuladosDashboard />} />
              <Route path="simulados/config" element={<AdminSimuladosConfig />} />

              {/* Sub-rotas de Eventos: guarda mais restrita que o resto de /admin - corretor
                  não tem acesso aqui (pedido explícito do cliente), diferente das demais
                  seções acima, que liberam pra todo o staff (allowedRoles do Route pai). */}
              <Route element={<ProtectedRoute allowedRoles={['admin', 'adm', 'professor', 'secretaria']} />}>
                <Route path="eventos" element={<AdminEventosReports />} />
                <Route path="eventos/new" element={<AdminEventosNew />} />
                <Route path="eventos/:eventoId/inscritos" element={<AdminEventosInscritos />} />
              </Route>

              {/* Redação com IA: mesma tela do aluno (StudentRedacaoIA), staff usa com o próprio uid.
                  Componente feito pro tema escuro do StudentLayout - sob o card claro do AdmLayout
                  (bg-white/50, p-6, rounded-2xl em AdmLayout.tsx) ficava com texto branco em fundo
                  branco. -m-6 estoura o padding do card pra cobrir ele por completo com o fundo
                  escuro esperado, mantendo o border-radius igual ao do card. */}
              <Route
                path="redacao-ia"
                element={
                  <div className="-m-6 rounded-2xl bg-[#0c141d] p-6">
                    <StudentRedacaoIA />
                  </div>
                }
              />
            </Route>
          </Route>

          {/* Student Protected Routes */}
          <Route element={<ProtectedRoute allowedRoles={['student', 'avulso']} />}>
            <Route path="/student" element={<StudentLayout />}>
              <Route index element={<StudentDashboard />} />
              <Route path="materials" element={<MaterialsHubLayout />}>
                <Route index element={<MaterialsOverview />} />
                <Route path="library" element={<StudentMaterials />} />
                <Route path="redacoes" element={<MyEssays />} />
                <Route path="conquistas" element={<Achievements />} />
              </Route>
              <Route path="schedules" element={<StudentSchedules />} />
              <Route path="payments" element={<StudentPayments />} />
              <Route path="packages" element={<StudentPackages />} />
              <Route path="contrato" element={<StudentContrato />} />
              <Route path="redacao-pacote" element={<StudentRedacaoPacote />} />
              <Route path="matricular" element={<StudentMatricular />} />
              <Route path="simulados" element={<StudentSimuladosDashboard />} />
              <Route path="redacao-ia" element={<StudentRedacaoIA />} />
            </Route>
          </Route>
        </Routes>
      </React.Suspense>
    </Router>
  );
};

export default AppRouter;
